import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { AjaxService } from 'AjaxService';

export const MAX_CHUNK_UPLOAD_FILE_SIZE = 104857600; // 100 MiB
export const CHUNK_UPLOAD_THRESHOLD = 5 * 1024 * 1024; // 5 MiB – darunter direkt, darüber Chunking
const CHUNK_RETRY_MAX = 3;

export interface ChunkUploadInitRequest {
  fileName: string;
  mimeType: string;
  fileSize: number;
  context: ChunkUploadContext;
}

export interface ChunkUploadContext {
  ownerId: number;
  entityArt: number;
  docHash: string;
  bezeichnung: string;
  lastModifiedDate: Date;
  isSelbstauskunftFile?: boolean;
}

export interface ChunkUploadInitResponse {
  uploadId: string;
  chunkSize: number;
  totalChunks: number;
}

export interface ChunkUploadCompleteResponse {
  dokumentId: number;
}

export interface StagedChunkUploadInitRequest {
  fileName: string;
  mimeType: string;
  fileSize: number;
  fieldName: string;
  targetRoute: string;
}

export interface StagedChunkUploadCompleteResponse {
  uploadToken: string;
}

export interface UploadHandle<T = void> {
  promise: Promise<T>;
  cancel: () => void;
}

@Injectable({ providedIn: 'root' })
export class ChunkedUploadService {
  private baseUrl = 'api/Dokument/Upload';
  private currentUploadId: string | null = null;
  private uploadRunning = false;

  constructor(private http: HttpClient, private ajaxService: AjaxService) {}

  /**
   * Lädt eine einzelne Datei hoch. Entscheidet automatisch, ob Chunking nötig ist.
   * Gibt ein Handle zurück, über das der Upload abgebrochen werden kann.
   */
  uploadFile(file: File, context: any, onProgress?: (pct: number) => void): UploadHandle {
    if (this.uploadRunning) {
      throw new Error('Es läuft bereits ein Datei-Upload.');
    }

    const abortController = new AbortController();
    const cancel$ = new Subject<void>();
    this.uploadRunning = true;

    const promise = this._uploadFile(file, context, abortController.signal, cancel$, onProgress)
      .finally(() => {
        this.uploadRunning = false;
        this.currentUploadId = null;
        cancel$.complete();
      });

    return {
      promise,
      cancel: () => {
        abortController.abort();
        cancel$.next();
        if (this.currentUploadId) {
          this.cancelSession(this.currentUploadId);
          this.currentUploadId = null;
        }
      },
    };
  }

  /**
   * Lädt eine Datei in den temporären Staging-Bereich und gibt ein einmaliges Token zurück.
   */
  stageFile(file: File, fieldName: string, targetRoute: string, onProgress?: (pct: number) => void): UploadHandle<string> {
    if (this.uploadRunning) {
      throw new Error('Es läuft bereits ein Datei-Upload.');
    }

    const abortController = new AbortController();
    const cancel$ = new Subject<void>();
    this.uploadRunning = true;

    const promise = this._stageFile(file, fieldName, targetRoute, abortController.signal, cancel$, onProgress)
      .finally(() => {
        this.uploadRunning = false;
        this.currentUploadId = null;
        cancel$.complete();
      });

    return {
      promise,
      cancel: () => {
        abortController.abort();
        cancel$.next();
        if (this.currentUploadId) {
          this.cancelSession(this.currentUploadId);
          this.currentUploadId = null;
        }
      },
    };
  }

  private async _uploadFile(file: File, context: any, signal: AbortSignal, cancel$: Subject<void>, onProgress?: (pct: number) => void): Promise<void> {
    if (file.size > MAX_CHUNK_UPLOAD_FILE_SIZE) {
      throw new Error(`Die Datei "${file.name}" überschreitet die maximal zulässige Größe von 100 MiB.`);
    }

    if (signal.aborted) throw new Error('Upload abgebrochen.');

    if (file.size <= CHUNK_UPLOAD_THRESHOLD) {
      return this.uploadSmall(file, context, cancel$);
    }

    return this.uploadChunked(file, context, signal, cancel$, onProgress);
  }

  /**
   * Bricht eine laufende Chunk-Session serverseitig ab.
   */
  cancelSession(uploadId: string): void {
    const options = this.ajaxService.getRequestHeaders();
    this.http.delete(`${this.baseUrl}/${uploadId}`, options).subscribe({ error: () => {} });
  }

  // -- interne Methoden --

  private async uploadSmall(file: File, context: any, cancel$: Subject<void>): Promise<void> {
    const formData = new FormData();
    formData.append(file.name, file);
    formData.append('context', JSON.stringify(context));

    const options = this.ajaxService.getRequestHeaders({ contentType: false });
    await lastValueFrom(
      this.http.post('api/Dokument/AddFiles', formData, options).pipe(takeUntil(cancel$))
    );
  }

  private async uploadChunked(file: File, context: any, signal: AbortSignal, cancel$: Subject<void>, onProgress?: (pct: number) => void): Promise<void> {
    // 1. Init
    const initReq: ChunkUploadInitRequest = {
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      fileSize: file.size,
      context: {
        ownerId: context.ownerId,
        entityArt: context.entityArt,
        docHash: context.docHash,
        bezeichnung: context.bezeichnung,
        lastModifiedDate: context.lastModifiedDate,
        isSelbstauskunftFile: context.isSelbstauskunftFile,
      },
    };

    const options = this.ajaxService.getRequestHeaders();
    const initResp = await lastValueFrom(
      this.http.post<ChunkUploadInitResponse>(`${this.baseUrl}/Init`, initReq, options).pipe(takeUntil(cancel$))
    ) as ChunkUploadInitResponse;

    const { uploadId, chunkSize, totalChunks } = initResp;
    this.currentUploadId = uploadId;

    if (signal.aborted) {
      this.cancelSession(uploadId);
      this.currentUploadId = null;
      throw new Error('Upload abgebrochen.');
    }

    // 2. Chunks senden
    let uploadedBytes = 0;

    for (let i = 0; i < totalChunks; i++) {
      if (signal.aborted) {
        this.cancelSession(uploadId);
        this.currentUploadId = null;
        throw new Error('Upload abgebrochen.');
      }

      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);

      await this.sendChunkWithRetry(uploadId, i, blob, signal, cancel$);

      uploadedBytes += (end - start);
      if (onProgress) {
        onProgress(Math.round((uploadedBytes / file.size) * 100));
      }
    }

    if (signal.aborted) {
      this.cancelSession(uploadId);
      this.currentUploadId = null;
      throw new Error('Upload abgebrochen.');
    }

    // 3. Complete
    await lastValueFrom(
      this.http.post<ChunkUploadCompleteResponse>(`${this.baseUrl}/${uploadId}/Complete`, {}, options).pipe(takeUntil(cancel$))
    );
    this.currentUploadId = null;
  }

  private async _stageFile(
    file: File,
    fieldName: string,
    targetRoute: string,
    signal: AbortSignal,
    cancel$: Subject<void>,
    onProgress?: (pct: number) => void
  ): Promise<string> {
    if (file.size > MAX_CHUNK_UPLOAD_FILE_SIZE) {
      throw new Error(`Die Datei "${file.name}" überschreitet die maximal zulässige Größe von 100 MiB.`);
    }
    if (file.size < 1) {
      throw new Error(`Die Datei "${file.name}" ist leer.`);
    }
    if (signal.aborted) {
      throw new Error('Upload abgebrochen.');
    }

    const initRequest: StagedChunkUploadInitRequest = {
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      fileSize: file.size,
      fieldName,
      targetRoute,
    };
    const options = this.ajaxService.getRequestHeaders();
    const initResponse = await lastValueFrom(
      this.http.post<ChunkUploadInitResponse>(`${this.baseUrl}/Staged/Init`, initRequest, options).pipe(takeUntil(cancel$))
    );

    const {uploadId, chunkSize, totalChunks} = initResponse;
    this.currentUploadId = uploadId;
    let uploadedBytes = 0;

    for (let index = 0; index < totalChunks; index++) {
      if (signal.aborted) {
        this.cancelSession(uploadId);
        throw new Error('Upload abgebrochen.');
      }

      const start = index * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      await this.sendChunkWithRetry(uploadId, index, file.slice(start, end), signal, cancel$);
      uploadedBytes += end - start;
      onProgress?.(Math.round((uploadedBytes / file.size) * 100));
    }

    if (signal.aborted) {
      this.cancelSession(uploadId);
      throw new Error('Upload abgebrochen.');
    }

    const completeResponse = await lastValueFrom(
      this.http.post<StagedChunkUploadCompleteResponse>(`${this.baseUrl}/${uploadId}/CompleteStaged`, {}, options).pipe(takeUntil(cancel$))
    );
    this.currentUploadId = null;
    return completeResponse.uploadToken;
  }

  private async sendChunkWithRetry(uploadId: string, index: number, blob: Blob, signal: AbortSignal, cancel$: Subject<void>): Promise<void> {
    let lastError: any;

    for (let attempt = 0; attempt < CHUNK_RETRY_MAX; attempt++) {
      if (signal.aborted) throw new Error('Upload abgebrochen.');

      try {
        const headers = this.ajaxService.getRequestHeaders({ contentType: false }).headers
          .set('Content-Type', 'application/octet-stream');
        await lastValueFrom(
          this.http.put(`${this.baseUrl}/${uploadId}/Chunk/${index}`, blob, { headers }).pipe(takeUntil(cancel$))
        );
        return;
      } catch (err) {
        lastError = err;
        if (attempt < CHUNK_RETRY_MAX - 1) {
          await this.delay(Math.pow(2, attempt) * 500);
        }
      }
    }

    throw lastError;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
